import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/lib/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  /** sm = 570px (ex.: temperatura), md = 672px, lg = 1008px (ex.: ativação de projeto). */
  size?: 'sm' | 'md' | 'lg';
  /** Modais mandatorios (ex.: ativacao de projeto — PRJ-036) nao fecham no ESC/backdrop. */
  dismissible?: boolean;
  /** Classes extras do corpo (ex.: altura mínima). */
  bodyClassName?: string;
  children: ReactNode;
}

const SIZES = { sm: 'max-w-[570px]', md: 'max-w-2xl', lg: 'max-w-[1008px]' } as const;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal do design system (Figma FGP: modal-header / modal-body / modal-footer).
 * Painel branco, borda gray-border, raio 12, padding 24 e gap 24 entre as seções.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  dismissible = true,
  bodyClassName,
  children,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  // ESC fecha; Tab fica preso dentro do modal.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, dismissible, onClose]);

  // Trava o scroll da página, foca o primeiro campo e devolve o foco ao fechar.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';

    const dialog = dialogRef.current;
    const firstField =
      dialog?.querySelector<HTMLElement>('input:not([disabled]), textarea:not([disabled]), select:not([disabled])') ??
      dialog?.querySelector<HTMLElement>(FOCUSABLE);
    (firstField ?? dialog)?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-source">
      <div
        className="absolute inset-0 bg-scrim/40"
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex max-h-[90vh] w-full flex-col gap-6 rounded-xl border border-line bg-surface p-6 outline-none',
          'shadow-[0px_4px_3px_rgba(0,0,0,0.1),0px_2px_2px_rgba(0,0,0,0.06)]',
          SIZES[size],
        )}
      >
        {/* modal-header */}
        <header className="flex flex-col gap-2">
          <div className="flex h-6 items-center justify-between gap-4">
            <h2 id={titleId} className="text-trim text-xl font-bold leading-none text-ink">
              {title}
            </h2>
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Fechar"
                className="-mr-2 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-xl hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-brand-outline"
              >
                <img src="/icons/x.svg" alt="" className="size-6" />
              </button>
            )}
          </div>
          {description && (
            <p id={descriptionId} className="text-trim text-base leading-none text-ink">
              {description}
            </p>
          )}
        </header>

        {/* modal-body */}
        <div className={cn('min-h-0 flex-1 overflow-y-auto overflow-x-clip', bodyClassName)}>
          {children}
        </div>

        {/* modal-footer */}
        {footer && <footer className="flex items-center justify-end gap-4">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
